import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { formatApiErrorBody } from '@/lib/formatApiError';
import { notify } from '@/lib/notify';
import { ContentPageHeader } from '@/components/global';
import { MetricGrid, SummaryMetricCard } from '@/components/global';
import { SlotGrid, SlotGridSlot } from '@/components/slots/SlotGrid';
import { RAGStatus } from '@/components/machines/RAGBadge';
import { MarginBadge } from '@/components/products';
import { getRagStatus } from '@/components/products/MarginBadge';
import {
  ProductSelect,
  ProductSelectOption,
  ProductSelectStatus,
} from '@/components/products/ProductSelect';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  PlanogramBuilderProductList,
  PlanogramExportMenu,
  PlanogramIncomePanel,
} from '@/components/planograms';
import { LayoutGrid, List, Save } from 'lucide-react';
import {
  PlanogramDetail,
  PlanogramExportAudience,
  computeBlendedMargin,
  computeLineMargin,
  downloadPlanogramExcel,
} from './planogram';

interface EditableSlot {
  id: string;
  slotNumber: number;
  slotLabel: string | null;
  maxCapacity: number;
  sellPriceOverride: number | null;
  listingFee: number;
  productId: string | null;
}

interface ProductInfo {
  sku: string;
  name: string;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  media: Array<{ url: string; mediaType: string }> | null;
  purchasePrice: number;
  sellPrice: number;
}

interface DerivedSlot {
  slot: EditableSlot;
  product: ProductInfo | null;
  cost: number | null;
  effectiveSell: number | null;
  margin: number | null;
}

const AUTOSAVE_DELAY_MS = 800;

function ragStatusForSlot(derived: DerivedSlot): RAGStatus {
  if (!derived.slot.productId || derived.margin == null) return 'AMBER';
  const status = getRagStatus(derived.margin);
  return status === 'green' ? 'GREEN' : status === 'amber' ? 'AMBER' : 'RED';
}

export default function PlanogramBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<PlanogramDetail | null>(null);
  const [slots, setSlots] = useState<EditableSlot[]>([]);
  const [productOptions, setProductOptions] = useState<ProductSelectOption[]>([]);
  const [productMap, setProductMap] = useState<Map<string, ProductInfo>>(new Map());
  const [view, setView] = useState<'grid' | 'list'>('list');
  const [pageTab, setPageTab] = useState<'planogram' | 'income'>('planogram');
  const [monthlyCogs, setMonthlyCogs] = useState(0);
  const [editingSlotId, setEditingSlotId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const revisionRef = useRef(0);
  const savingRef = useRef(false);
  const queuedSaveRef = useRef(false);
  const slotsRef = useRef<EditableSlot[]>([]);
  const monthlyCogsRef = useRef(0);
  const dirtyRef = useRef(false);

  const applyTemplate = useCallback((detail: PlanogramDetail) => {
    setTemplate(detail);
    setMonthlyCogs(detail.monthlyCogs ?? 0);
    setSlots(
      detail.slots.map((s) => ({
        id: s.id,
        slotNumber: s.slotNumber,
        slotLabel: s.slotLabel,
        maxCapacity: s.maxCapacity,
        sellPriceOverride: s.sellPriceOverride,
        listingFee: s.listingFee ?? 0,
        productId: s.productId,
      }))
    );
    dirtyRef.current = false;
    setDirty(false);
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [templateRes, productsRes] = await Promise.all([
        apiFetch(`/api/v1/planograms/${id}`),
        apiFetch('/api/v1/products?pageSize=1000'),
      ]);

      if (!templateRes.ok) {
        if (templateRes.status === 404) throw new Error('Planogram not found');
        throw new Error('Failed to load planogram');
      }
      const detail: PlanogramDetail = await templateRes.json();
      applyTemplate(detail);

      if (productsRes.ok) {
        const data = await productsRes.json();
        const items = (data.items ?? []) as Array<{
          id: string;
          sku: string;
          name: string;
          imageUrl?: string | null;
          thumbnailUrl?: string | null;
          media?: Array<{ url: string; mediaType: string }> | null;
          purchasePrice: number | string;
          sellPrice: number | string;
          status?: ProductSelectStatus;
        }>;
        const options: ProductSelectOption[] = [];
        const map = new Map<string, ProductInfo>();
        for (const p of items) {
          options.push({
            id: p.id,
            sku: p.sku,
            name: p.name,
            imageUrl: p.imageUrl ?? null,
            thumbnailUrl: p.thumbnailUrl ?? null,
            media: p.media ?? null,
            status: p.status,
          });
          map.set(p.id, {
            sku: p.sku,
            name: p.name,
            imageUrl: p.imageUrl ?? null,
            thumbnailUrl: p.thumbnailUrl ?? null,
            media: p.media ?? null,
            purchasePrice: Number(p.purchasePrice) || 0,
            sellPrice: Number(p.sellPrice) || 0,
          });
        }
        setProductOptions(options);
        setProductMap(map);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load planogram');
    } finally {
      setLoading(false);
    }
  }, [id, applyTemplate]);

  useEffect(() => {
    load();
  }, [load]);

  const targetMargin = template?.targetMarginPercent ?? 60;

  const derive = useCallback(
    (slot: EditableSlot): DerivedSlot => {
      const product = slot.productId ? productMap.get(slot.productId) ?? null : null;
      const cost = product ? product.purchasePrice : null;
      const effectiveSell = product
        ? slot.sellPriceOverride ?? product.sellPrice
        : null;
      const margin =
        cost != null && effectiveSell != null ? computeLineMargin(cost, effectiveSell) : null;
      return { slot, product, cost, effectiveSell, margin };
    },
    [productMap]
  );

  const derivedSlots = useMemo(() => slots.map(derive), [slots, derive]);

  const incomeSlots = useMemo(
    () =>
      derivedSlots.map((d) => ({
        id: d.slot.id,
        slotNumber: d.slot.slotNumber,
        slotLabel: d.slot.slotLabel,
        maxCapacity: d.slot.maxCapacity,
        listingFee: d.slot.listingFee,
        productId: d.slot.productId,
        productName: d.product?.name ?? null,
        product: d.product,
        effectiveSell: d.effectiveSell,
        cost: d.cost,
      })),
    [derivedSlots]
  );

  const metrics = useMemo(() => {
    const filled = derivedSlots.filter((d) => d.slot.productId != null);
    const blended = computeBlendedMargin(derivedSlots.map((d) => d.margin));
    const prices = filled
      .map((d) => d.effectiveSell)
      .filter((p): p is number => p != null);
    const avgPrice =
      prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : null;
    return {
      slotsFilled: filled.length,
      blended,
      avgPrice,
      meetsTarget: blended != null && blended >= targetMargin,
    };
  }, [derivedSlots, targetMargin]);

  slotsRef.current = slots;
  monthlyCogsRef.current = monthlyCogs;
  dirtyRef.current = dirty;

  const markDirty = useCallback(() => {
    revisionRef.current += 1;
    dirtyRef.current = true;
    setDirty(true);
    setSaveError(null);
  }, []);

  const updateSlot = useCallback((slotId: string, patch: Partial<EditableSlot>) => {
    setSlots((prev) => prev.map((s) => (s.id === slotId ? { ...s, ...patch } : s)));
    markDirty();
  }, [markDirty]);

  const setProduct = useCallback(
    (slotId: string, productId: string | null) => {
      updateSlot(slotId, { productId, ...(productId ? {} : { sellPriceOverride: null }) });
    },
    [updateSlot]
  );

  const setCapacity = useCallback(
    (slotId: string, raw: string) => {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n) && n >= 1) updateSlot(slotId, { maxCapacity: n });
    },
    [updateSlot]
  );

  const setSellOverride = useCallback(
    (slotId: string, raw: string) => {
      const trimmed = raw.trim();
      if (trimmed === '') {
        updateSlot(slotId, { sellPriceOverride: null });
        return;
      }
      const n = parseFloat(trimmed);
      if (Number.isFinite(n) && n >= 0) updateSlot(slotId, { sellPriceOverride: n });
    },
    [updateSlot]
  );

  const setListingFee = useCallback(
    (slotId: string, raw: string) => {
      const trimmed = raw.trim();
      if (trimmed === '') {
        updateSlot(slotId, { listingFee: 0 });
        return;
      }
      const n = parseFloat(trimmed);
      if (Number.isFinite(n) && n >= 0) updateSlot(slotId, { listingFee: n });
    },
    [updateSlot]
  );

  const setGroupListingFee = useCallback(
    (slotIds: string[], raw: string) => {
      for (const slotId of slotIds) {
        setListingFee(slotId, raw);
      }
    },
    [setListingFee]
  );

  const setMonthlyCogsValue = useCallback((raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === '') {
      setMonthlyCogs(0);
      markDirty();
      return;
    }
    const n = parseFloat(trimmed);
    if (Number.isFinite(n) && n >= 0) {
      setMonthlyCogs(n);
      markDirty();
    }
  }, [markDirty]);

  const persist = useCallback(async (options?: { silent?: boolean }): Promise<boolean> => {
    const planogramId = id?.trim();
    if (!planogramId) {
      notify.error('Planogram id is missing');
      return false;
    }
    if (options?.silent && !dirtyRef.current) return true;
    if (savingRef.current) {
      queuedSaveRef.current = true;
      return false;
    }

    savingRef.current = true;
    setSaving(true);
    setError(null);
    const revision = revisionRef.current;
    const currentSlots = slotsRef.current;
    const currentCogs = monthlyCogsRef.current;

    try {
      const patchRes = await apiFetch(`/api/v1/planograms/${planogramId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monthlyCogs: currentCogs }),
      });
      if (!patchRes.ok) {
        throw new Error(formatApiErrorBody(await patchRes.json().catch(() => ({}))));
      }

      const payload = {
        slots: currentSlots.map((s) => ({
          slotId: s.id,
          productId: s.productId,
          maxCapacity: s.maxCapacity,
          sellPriceOverride: s.sellPriceOverride,
          listingFee: s.listingFee,
        })),
      };
      const res = await apiFetch(`/api/v1/planograms/${planogramId}/slots`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(formatApiErrorBody(await res.json().catch(() => ({}))));
      const detail: PlanogramDetail = await res.json();
      if (revisionRef.current === revision) {
        setTemplate(detail);
        dirtyRef.current = false;
        setDirty(false);
        setSaveError(null);
        setSavedOnce(true);
      }
      if (!options?.silent) notify.success('Planogram saved');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save planogram';
      setSaveError(message);
      notify.error(message);
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
      if (queuedSaveRef.current) {
        queuedSaveRef.current = false;
        void persist({ silent: true });
      }
    }
  }, [id]);

  const handleSave = useCallback(() => {
    void persist();
  }, [persist]);

  useEffect(() => {
    if (!dirty) return;
    const timer = window.setTimeout(() => {
      void persist({ silent: true });
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [dirty, slots, monthlyCogs, persist]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && dirtyRef.current) {
        void persist({ silent: true });
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [persist]);

  const handleExport = useCallback(async (audience: PlanogramExportAudience) => {
    const planogramId = id?.trim();
    if (!planogramId) {
      notify.error('Planogram id is missing');
      return;
    }
    setExporting(true);
    try {
      if (dirtyRef.current) {
        await persist({ silent: true });
      }
      await downloadPlanogramExcel(planogramId, audience);
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to export planogram');
    } finally {
      setExporting(false);
    }
  }, [id, persist]);

  const gridSlots: SlotGridSlot[] = useMemo(
    () =>
      derivedSlots.map((d) => ({
        id: d.slot.id,
        slotNumber: d.slot.slotNumber,
        slotLabel: d.slot.slotLabel,
        productId: d.slot.productId,
        productName: d.product?.name,
        product: d.product
          ? {
              id: d.slot.productId as string,
              name: d.product.name,
              sku: d.product.sku,
              imageUrl: d.product.imageUrl,
              media: d.product.media ?? undefined,
            }
          : null,
        currentQuantity: 0,
        maxCapacity: d.slot.maxCapacity,
        ragStatus: { status: ragStatusForSlot(d), percentageFilled: 0 },
        footer: (
          <div className="flex items-center justify-between gap-2">
            <span className="tabular-nums">
              {d.effectiveSell != null ? `£${d.effectiveSell.toFixed(2)}` : '—'}
            </span>
            {d.margin != null ? (
              <MarginBadge marginPercent={d.margin} targetMargin={targetMargin} />
            ) : (
              <span className="text-muted-foreground">
                {d.slot.productId ? 'Cost n/a' : 'No product'}
              </span>
            )}
          </div>
        ),
      })),
    [derivedSlots, targetMargin]
  );

  const editingDerived = editingSlotId
    ? derivedSlots.find((d) => d.slot.id === editingSlotId) ?? null
    : null;

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading planogram…</p>
      </div>
    );
  }

  if (error && !template) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 text-center">
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" onClick={() => navigate('/planograms')}>
          Back to planograms
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ContentPageHeader
        title={template?.name ?? 'Planogram'}
        back={{ to: '/planograms', label: 'Planograms' }}
        description={
          template?.description ? (
            <span>{template.description}</span>
          ) : (
            <span>Assign products and tune prices to hit the blended margin target.</span>
          )
        }
        actions={
          <>
            <span className="text-xs text-muted-foreground" aria-live="polite">
              {saving
                ? 'Saving…'
                : saveError
                  ? 'Save failed'
                  : dirty
                    ? 'Unsaved changes'
                    : savedOnce
                      ? 'Saved'
                      : null}
            </span>
            <PlanogramExportMenu onExport={(audience) => void handleExport(audience)} disabled={exporting} />
            <Button onClick={handleSave} disabled={!dirty || saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      />

      <Tabs value={pageTab} onValueChange={(v) => setPageTab(v as 'planogram' | 'income')}>
        <TabsList>
          <TabsTrigger value="planogram">Planogram</TabsTrigger>
          <TabsTrigger value="income">Income projections</TabsTrigger>
        </TabsList>

        <TabsContent value="planogram" className="mt-6 space-y-6">
          <MetricGrid className="lg:grid-cols-3">
            <SummaryMetricCard
              label={`Blended margin (target ${targetMargin.toFixed(0)}%)`}
              value={metrics.blended != null ? `${metrics.blended.toFixed(1)}%` : '—'}
              status={metrics.blended == null ? undefined : metrics.meetsTarget ? 'green' : 'red'}
            />
            <SummaryMetricCard
              label="Slots filled"
              value={`${metrics.slotsFilled}/${slots.length}`}
            />
            <SummaryMetricCard
              label="Projected avg price"
              value={metrics.avgPrice != null ? `£${metrics.avgPrice.toFixed(2)}` : '—'}
            />
          </MetricGrid>

          <div className="flex items-center gap-3">
            <Tabs value={view} onValueChange={(v) => setView(v as 'grid' | 'list')}>
              <TabsList>
                <TabsTrigger value="list" className="gap-1.5">
                  <List className="h-4 w-4" />
                  List
                </TabsTrigger>
                <TabsTrigger value="grid" className="gap-1.5">
                  <LayoutGrid className="h-4 w-4" />
                  Grid
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {view === 'grid' ? (
            <SlotGrid
              slots={gridSlots}
              columns={5}
              onSlotClick={(slot) => setEditingSlotId(slot.id)}
            />
          ) : (
            <Card className="overflow-hidden">
              <PlanogramBuilderProductList
                slots={derivedSlots}
                targetMargin={targetMargin}
                onSlotClick={setEditingSlotId}
                onListingFeeChange={setGroupListingFee}
              />
            </Card>
          )}
        </TabsContent>

        <TabsContent value="income" className="mt-6">
          <PlanogramIncomePanel
            slots={incomeSlots}
            monthlyCogs={monthlyCogs}
            targetMargin={targetMargin}
            onMonthlyCogsChange={setMonthlyCogsValue}
            onListingFeeChange={setGroupListingFee}
          />
        </TabsContent>
      </Tabs>

      <Dialog
        open={editingSlotId != null}
        onOpenChange={(open) => !open && setEditingSlotId(null)}
      >
        <DialogContent className="overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>
              Slot {editingDerived?.slot.slotLabel ?? `#${editingDerived?.slot.slotNumber ?? ''}`}
            </DialogTitle>
          </DialogHeader>
          {editingDerived ? (
            <div className="min-w-0 space-y-4">
              <div className="min-w-0 space-y-2">
                <Label>Product</Label>
                <ProductSelect
                  value={editingDerived.slot.productId ?? ''}
                  onValueChange={(v) => setProduct(editingDerived.slot.id, v || null)}
                  products={productOptions}
                  allowEmpty
                  emptyLabel="No product"
                  onUnavailableSelect={(product) => {
                    window.open(
                      `/inventory/products/${product.id}/edit`,
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                />
              </div>
              <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="min-w-0 space-y-2">
                  <Label htmlFor="dialog-capacity">Capacity</Label>
                  <Input
                    id="dialog-capacity"
                    type="number"
                    min={1}
                    max={100}
                    className="min-w-0"
                    value={editingDerived.slot.maxCapacity}
                    onChange={(e) => setCapacity(editingDerived.slot.id, e.target.value)}
                  />
                </div>
                <div className="min-w-0 space-y-2">
                  <Label htmlFor="dialog-cost">Cost price (£)</Label>
                  <Input
                    id="dialog-cost"
                    type="text"
                    inputMode="decimal"
                    className="min-w-0"
                    value={
                      editingDerived.cost != null && editingDerived.cost > 0
                        ? editingDerived.cost.toFixed(2)
                        : ''
                    }
                    placeholder="—"
                    disabled
                    title="Unit purchase cost from the assigned product in Inventory. Update the product’s purchase price to change it."
                  />
                </div>
                <div className="min-w-0 space-y-2">
                  <Label htmlFor="dialog-sell">Sell price (£)</Label>
                  <Input
                    id="dialog-sell"
                    type="number"
                    min={0}
                    step="0.01"
                    className="min-w-0"
                    value={editingDerived.slot.sellPriceOverride ?? ''}
                    placeholder={
                      editingDerived.product
                        ? editingDerived.product.sellPrice.toFixed(2)
                        : '—'
                    }
                    disabled={!editingDerived.product}
                    onChange={(e) => setSellOverride(editingDerived.slot.id, e.target.value)}
                  />
                </div>
                <div className="min-w-0 space-y-2">
                  <Label htmlFor="dialog-listing-fee">Listing fee (£)</Label>
                  <Input
                    id="dialog-listing-fee"
                    type="number"
                    min={0}
                    step="0.01"
                    className="min-w-0"
                    value={editingDerived.slot.listingFee > 0 ? editingDerived.slot.listingFee : ''}
                    placeholder="0.00"
                    onChange={(e) => setListingFee(editingDerived.slot.id, e.target.value)}
                  />
                </div>
              </div>
              <div className="flex min-w-0 flex-col gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                <span className="shrink-0 text-muted-foreground">Line margin</span>
                {editingDerived.margin != null ? (
                  <MarginBadge
                    marginPercent={editingDerived.margin}
                    targetMargin={targetMargin}
                    showTarget
                  />
                ) : (
                  <span className="min-w-0 text-muted-foreground sm:text-right">
                    {editingDerived.slot.productId
                      ? 'No purchase cost set on this product'
                      : 'Assign a product'}
                  </span>
                )}
              </div>
            </div>
          ) : null}
          <DialogFooter className="gap-2 sm:justify-between">
            {editingDerived?.slot.productId ? (
              <Button
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => {
                  setProduct(editingDerived.slot.id, null);
                  setEditingSlotId(null);
                }}
              >
                Clear product
              </Button>
            ) : (
              <span />
            )}
            <Button onClick={() => setEditingSlotId(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
